"use client";

import useSWR from "swr";
import { useGraphql } from "~/api-client";
import { Location } from "~/types";
import { mapLocation } from "~/utils/location";
import { useMemo } from "react";

export function useLocation(
  locationRef?: string,
  options?: { revalidateOnMount?: boolean; revalidateOnFocus?: boolean },
): {
  location: Location | undefined;
  merchantName: string | undefined;
  loading: boolean;
} {
  const { getLocation } = useGraphql();
  const { data, isLoading } = useSWR(
    ["location", locationRef],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_p1, p2]) => (p2 ? getLocation(p2) : null),
    options,
  );

  return useMemo(
    () => ({
      loading: isLoading,
      location: data?.merchant.location
        ? mapLocation(data?.merchant.location)
        : undefined,
      merchantName: data?.merchant.name,
    }),
    [data, isLoading],
  );
}

export function useLocations(
  currentCoords?: {
    latitude: number;
    longitude: number;
  },
  options?: { revalidateOnMount?: boolean; revalidateOnFocus?: boolean },
): {
  merchantName: string | undefined;
  locations: Location[] | undefined;
  loading: boolean;
} {
  const { getLocations } = useGraphql();
  const { data, isLoading } = useSWR(
    [
      "locations",
      currentCoords?.latitude || null,
      currentCoords?.longitude || null,
    ],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_name, lat, lng]) => getLocations(lat, lng),
    options,
  );
  const locations = useMemo(
    () => (data?.merchant.locations || []).map(mapLocation),
    [data],
  );

  return {
    loading: isLoading,
    merchantName: data?.merchant.name,
    locations,
  };
}
